const DIRECTIONS = new Set(['superior', 'peer', 'child']);
const ITEM_TYPES = new Set(['chapter', 'section', 'subsection']);

const positiveId = (value) => {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
};

const createTarget = ({
  direction,
  sourceType,
  sourceId,
  entityType,
  chapterId = null,
  sectionId = null,
  parentId = null,
  position,
  afterId = null,
}) => ({
  actionKey: `${sourceType}:${sourceId}:${direction}`,
  sourceKey: `${sourceType}:${sourceId}`,
  sourceAnchorId: `${sourceType}-${sourceId}`,
  entityType,
  chapterId,
  sectionId,
  parentId,
  position,
  afterId,
});

export const buildHierarchyCreateTarget = (context, direction) => {
  if (!context || !DIRECTIONS.has(direction) || !ITEM_TYPES.has(context.currentType)) {
    return null;
  }

  const sourceType = context.currentType;
  const sourceId = positiveId(context.currentItem?.id);
  const chapterId = positiveId(context.chapter?.id);

  if (!sourceId || !chapterId) return null;

  if (sourceType === 'chapter') {
    if (sourceId !== chapterId || direction === 'superior') return null;

    return direction === 'peer'
      ? createTarget({
          direction,
          sourceType,
          sourceId,
          entityType: 'chapter',
          position: 'after',
          afterId: sourceId,
        })
      : createTarget({
          direction,
          sourceType,
          sourceId,
          entityType: 'section',
          chapterId,
          position: 'last',
        });
  }

  const sectionId = positiveId(context.section?.id);
  if (!sectionId) return null;

  if (sourceType === 'section') {
    if (sourceId !== sectionId) return null;

    if (direction === 'superior') {
      return createTarget({
        direction,
        sourceType,
        sourceId,
        entityType: 'chapter',
        position: 'after',
        afterId: chapterId,
      });
    }

    if (direction === 'peer') {
      return createTarget({
        direction,
        sourceType,
        sourceId,
        entityType: 'section',
        chapterId,
        position: 'after',
        afterId: sectionId,
      });
    }

    return createTarget({
      direction,
      sourceType,
      sourceId,
      entityType: 'subsection',
      chapterId,
      sectionId,
      position: 'last',
    });
  }

  const ancestors = Array.isArray(context.subsectionAncestors)
    ? context.subsectionAncestors
    : [];
  const ancestorIds = ancestors.map((ancestor) => positiveId(ancestor?.id));
  if (ancestorIds.some((id) => !id)) return null;

  if (direction === 'child') {
    return createTarget({
      direction,
      sourceType,
      sourceId,
      entityType: 'subsection',
      chapterId,
      sectionId,
      parentId: sourceId,
      position: 'last',
    });
  }

  const directParentId = ancestorIds.at(-1) ?? null;

  if (direction === 'peer') {
    return createTarget({
      direction,
      sourceType,
      sourceId,
      entityType: 'subsection',
      chapterId,
      sectionId,
      parentId: directParentId,
      position: 'after',
      afterId: sourceId,
    });
  }

  if (!directParentId) {
    return createTarget({
      direction,
      sourceType,
      sourceId,
      entityType: 'section',
      chapterId,
      position: 'after',
      afterId: sectionId,
    });
  }

  return createTarget({
    direction,
    sourceType,
    sourceId,
    entityType: 'subsection',
    chapterId,
    sectionId,
    parentId: ancestorIds.at(-2) ?? null,
    position: 'after',
    afterId: directParentId,
  });
};
